<template>
    <div class="video-formatter-page">
      <header class="vf-header">
        <div class="vf-header-inner">
            <h1 class="vf-subtitle">{{ $t('lbl_EMPBE_VideoFormatter_PoweredBy') }}</h1>
          <div>
            <a href="./dashboard/default" target="_blank">
                <img src="https://cdn.adsalecdn.com/EMP/2-0/images/EMP_logos_tc.png?x-oss-process=image/format,webp" height="40">
            </a>
          </div>
        </div>
      </header>
  
      <main class="vf-main">
        <div class="vf-container">
          <h1 class="vf-title">{{ $t('lbl_EMPBE_VideoFormatter_Name') }}</h1>
          <div class="vf-card">
            <div
              class="vf-dropzone"
              :class="{ 'vf-drop-active': dropActive }"
              @click="triggerFileInput"
              @dragover.prevent="dropActive = true; dropText = $t('lbl_EMPBE_VideoFormatter_DragAndDropVideoFilesHere')"
              @dragleave.prevent="dropActive = false; dropText = $t('lbl_EMPBE_VideoFormatter_DragAndDropVideoFilesHere')"
              @drop.prevent="onDrop"
            >
              <div class="vf-drop-icon-wrap">
                <svg class="vf-drop-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
              </div>
              <div class="vf-drop-text-wrap">
                <p class="vf-drop-text">{{ dropText }}</p>
                <p class="vf-drop-hint">{{ $t('lbl_EMPBE_VideoFormatter_Specification') }}</p>
              </div>
              <button type="button" class="vf-browse-btn">{{ $t('lbl_EMPBE_VideoFormatter_BrowseFiles') }}</button>
              <input
                ref="fileInput"
                type="file"
                accept="video/*,.avi,.mkv,.mov,.wmv,.flv,.webm,.mpg,.mpeg,.m4v,.ts,.3gp,.ogv,.rm,.rmvb,.vob,.asf,.divx,.f4v"
                multiple
                class="vf-file-input"
                @change="onFileChange"
              >
            </div>
          </div>
  
          <div v-if="jobs.length > 0" class="vf-jobs">
            <div v-if="activeJobs.length > 0" class="vf-job-group">
              <h2 class="vf-group-title">{{ $t('lbl_EMPBE_VideoFormatter_InProgress') }}</h2>
              <div v-for="job in activeJobs" :key="job.id" class="vf-job-card">
                <div class="vf-job-icon vf-job-icon-loading">
                  <svg class="vf-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="vf-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                    <path class="vf-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                </div>
                <div class="vf-job-body">
                  <div class="vf-job-row">
                    <p class="vf-job-filename">{{ job.originalName }}</p>
                    <span class="vf-job-size">{{ formatBytes(job.originalSize) }}</span>
                  </div>
                  <div class="vf-progress-bar">
                    <div class="vf-progress-fill" :style="{ width: clampedProgress(job) + '%' }"/>
                  </div>
                  <p class="vf-job-status">{{ statusText(job) }}</p>
                  <p v-if="job.status === 'processing' && getProgressHint(clampedProgress(job), job.originalSize)" class="vf-job-hint">
                    {{ getProgressHint(clampedProgress(job), Math.round(job.originalSize / (1024 * 1024))) }}
                  </p>
                </div>
              </div>
              <div class="vf-stay-notice">
                <span class="vf-notice-icon">⚠</span>
                <div>
                  <p class="vf-notice-title">{{ $t('lbl_EMPBE_VideoFormatter_Warning1') }}</p>
                  <p class="vf-notice-text">{{ $t('lbl_EMPBE_VideoFormatter_Warning2') }}</p>
                </div>
              </div>
            </div>
  
            <div v-if="completedJobs.length > 0" class="vf-job-group">
              <h2 class="vf-group-title">{{ $t('lbl_EMPBE_VideoFormatter_Completed') }}</h2>
              <div v-for="job in completedJobs" :key="job.id" class="vf-job-card">
                <div class="vf-job-icon vf-job-icon-done">✓</div>
                <div class="vf-job-body">
                  <div class="vf-job-row vf-job-row-actions">
                    <p class="vf-job-filename">{{ job.originalName }}</p>
                    <div class="vf-actions">
                      <button type="button" class="vf-btn-dismiss" @click="dismissJob(job.id)">✕</button>
                      <button type="button" class="vf-btn-download" @click="downloadJob(job.id)">{{ $t('lbl_EMPBE_VideoFormatter_Download') }}</button>
                    </div>
                  </div>
                  <p class="vf-job-meta">
                    {{ formatBytes(job.originalSize) }} → {{ formatBytes(job.outputSize || 0) }}
                    <span v-if="job.elapsed"> · {{ job.elapsed }}</span>
                  </p>
                </div>
              </div>
            </div>
  
            <div v-if="errorJobs.length > 0" class="vf-job-group">
              <h2 class="vf-group-title">{{ $t('lbl_EMPBE_VideoFormatter_Failed') }}</h2>
              <div v-for="job in errorJobs" :key="job.id" class="vf-job-card">
                <div class="vf-job-icon vf-job-icon-error">!</div>
                <div class="vf-job-body">
                  <div class="vf-job-row vf-job-row-actions">
                    <p class="vf-job-filename">{{ job.originalName }}</p>
                    <button type="button" class="vf-btn-dismiss" @click="dismissJob(job.id)">{{ $t('lbl_EMPBE_VideoFormatter_Clear') }}</button>
                  </div>
                  <p class="vf-job-error" v-if="job.error">{{ $t('lbl_EMPBE_VideoFormatter_video_conversion_failed') }}</p>
                </div>
              </div>
            </div>
          </div>
          <div v-show="jobs.length === 0" class="vf-empty">
            <p class="vf-empty-text">{{ $t('lbl_EMPBE_VideoFormatter_Notice') }}</p>
          </div>
        </div>
      </main>
  
      <footer class="vf-footer">
        <p class="vf-footer-text">{{ $t('lbl_EMPBE_VideoFormatter_Copyright', { currentYear: new Date().getFullYear() }) }}</p>
      </footer>
  
      <div v-if="showSizeModal" class="vf-modal-overlay" @click.self="showSizeModal = false">
        <div class="vf-modal">
          <div class="vf-modal-body">
            <div class="vf-modal-icon-wrap">
              <span class="vf-modal-icon">!</span>
            </div>
            <h3 class="vf-modal-title">{{ $t('lbl_EMPBE_VideoFormatter_FileTooLarge') }}</h3>
            <p class="vf-modal-message">
              <strong>{{ sizeModalFileName }}</strong> {{ $t('lbl_EMPBE_VideoFormatter_ExceedsMaximumFileSize') }}
            </p>
          </div>
          <div class="vf-modal-footer">
            <button type="button" class="vf-btn-primary" @click="showSizeModal = false">{{ $t('lbl_EMPBE_VideoFormatter_OK') }}</button>
          </div>
        </div>
      </div>

      <div v-if="showRejectedModal" class="vf-modal-overlay" @click.self="showRejectedModal = false">
        <div class="vf-modal">
          <div class="vf-modal-body">
            <div class="vf-modal-icon-wrap">
              <span class="vf-modal-icon">!</span>
            </div>
            <h3 class="vf-modal-title">{{ $t('lbl_EMPBE_VideoFormatter_NotAVideoFile') }}</h3>
            <div class="vf-modal-message">
              <p><strong>{{ rejectedFileName }}</strong></p>
              <p class="vf-modal-message-reject-reason">
                {{ rejectedFileReason }}
              </p>
            </div>
          </div>
          <div class="vf-modal-footer">
            <button type="button" class="vf-btn-primary" @click="showRejectedModal = false">{{ $t('lbl_EMPBE_VideoFormatter_OK') }}</button>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import {
    createFFmpegInstance,
    getDuration,
    formatBytes as fmtBytes,
    formatDuration as fmtDuration,
    getProgressHint as getHint,
    classifyError,
    fetchFileData,
    createJobId,
    MAX_FILE_SIZE,
    NUM_SEGMENTS,
    MAX_WORKERS,
    SPEED_PRESET,
    VIDEO_FILTER_SAME_RESOLUTION
  } from '@/assets/js/ffmpegConverter';
  
  export default {
    name: 'VideoFormatter',
    data() {
      return {
        jobs: [],
        conversionQueue: [],
        isConverting: false,
        bannerVisible: false,
        bannerTitle: '',
        bannerSubtitle: '',
        dropActive: false,
        dropText: '',
        showSizeModal: false,
        sizeModalFileName: '',
        sizeModalFileSize: 0,
        showRejectedModal: false,
        rejectedFileName: '',
        rejectedFileReason: ''
      };
    },
    created() {
      this.dropText = this.$t('lbl_EMPBE_VideoFormatter_DragAndDropVideoFilesHere');
    },
    computed: {
      activeJobs() {
        return this.jobs.filter(j => j.status === 'loading' || j.status === 'processing');
      },
      completedJobs() {
        return this.jobs.filter(j => j.status === 'completed');
      },
      errorJobs() {
        return this.jobs.filter(j => j.status === 'error');
      },
      acceptedFileFormats() {
        return ['avi', 'mkv', 'mov', 'wmv', 'flv', 'webm', 'mpg', 'mpeg', 'm4v', 'mp4', 'ts', '3gp', 'ogv', 'rm', 'rmvb', 'vob', 'asf', 'divx', 'f4v'];
      },
    },
    methods: {
      formatBytes: fmtBytes,
      formatDuration(ms) {
        return fmtDuration(ms, { lessThan1s: this.$t('lbl_EMPBE_VideoFormatter_LessThan1s') });
      },
      getProgressHint(progress, fileSizeMB) {
        return getHint(progress, fileSizeMB, this._progressMessages());
      },
  
      clampedProgress(job) {
        const p = job.progress != null ? job.progress : 0;
        return Math.min(Math.max(p, 0), 100);
      },
  
      statusText(job) {
        if (job.status === 'loading') return this.$t('lbl_EMPBE_VideoFormatter_Status_LoadingEngine');
        if (job.progress >= 0) return `${job.phase || this.$t('lbl_EMPBE_VideoFormatter_Phase_ConvertingToMP4')} ${this.clampedProgress(job)}%`;
        return job.phase || this.$t('lbl_EMPBE_VideoFormatter_Phase_Preparing');
      },

  
      hideBanner() {
        this.bannerVisible = false;
      },
  
      triggerFileInput() {
        this.$refs.fileInput && this.$refs.fileInput.click();
      },
  
      onFileChange(e) {
        const files = e.target.files;
        if (files && files.length) this.handleFiles(Array.from(files));
        e.target.value = '';
      },
  
      onDrop(e) {
        this.dropActive = false;
        this.dropText = this.$t('lbl_EMPBE_VideoFormatter_DragAndDropVideoFilesHere');
        if (e.dataTransfer && e.dataTransfer.files.length) {
          this.handleFiles(Array.from(e.dataTransfer.files));
        }
      },
  
      handleFiles(files) {
        files.forEach(file => this.enqueueFile(file));
      },

      isVideoFile(file) {
        if (!file || !file.name) return false;
        const type = (file.type || '').toLowerCase();
        if (type.startsWith('video/')) return true;
        const ext = file.name.split('.').pop().toLowerCase();
        return this.acceptedFileFormats.indexOf(ext) !== -1;
      },

      enqueueFile(file) {
        if (!this.isVideoFile(file)) {
          this.rejectedFileName = file.name;
          const acceptedFormatStr = this.acceptedFileFormats.join(', ');
          this.rejectedFileReason = this.$t('lbl_EMPBE_VideoFormatter_NotAVideoFile_Reason', { fileFormat: acceptedFormatStr });
          this.showRejectedModal = true;
          return;
        }
        if (file.size > MAX_FILE_SIZE) {
          this.sizeModalFileName = file.name;
          this.sizeModalFileSize = file.size;
          this.showSizeModal = true;
          return;
        }
        const job = {
          id: createJobId(),
          originalName: file.name,
          originalSize: file.size,
          status: 'loading',
          progress: 0,
          phase: '',
          outputBlob: null,
          outputSize: 0,
          error: '',
          elapsed: '',
          segmentCount: 0,
          file,
          speed: 'fast'
        };
        this.jobs.push(job);
        this.conversionQueue.push(job.id);
        this.processQueue();
      },
  
      downloadJob(id) {
        const job = this.jobs.find(j => j.id === id);
        if (!job || !job.outputBlob) return;
        const url = URL.createObjectURL(job.outputBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = job.originalName.replace(/\.[^.]+$/, '') + '.mp4';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      },
  
      dismissJob(id) {
        const job = this.jobs.find(j => j.id === id);
        if (job && job.outputBlob) job.outputBlob = null;
        this.jobs = this.jobs.filter(j => j.id !== id);
      },
  
      async processQueue() {
        if (this.isConverting || this.conversionQueue.length === 0) return;
        this.isConverting = true;
  
        while (this.conversionQueue.length > 0) {
          const jobId = this.conversionQueue.shift();
          const job = this.jobs.find(j => j.id === jobId);
          if (!job) continue;
  
          const allLogs = [];
          const startTime = performance.now();
          const instancesToCleanup = [];
  
          try {
            job.phase = this.$t('lbl_EMPBE_VideoFormatter_Phase_Preparing');

            const mainFFmpeg = await createFFmpegInstance();
            instancesToCleanup.push(mainFFmpeg);
            mainFFmpeg.on('log', (e) => { allLogs.push(e.message); });
  
            this.hideBanner();
            job.status = 'processing';
            job.phase = this.$t('lbl_EMPBE_VideoFormatter_Phase_Analyzing');
            job.progress = 0;
  
            const ext = (job.file && job.file.name) ? job.file.name.split('.').pop() : 'vid';
            const inputName = 'input.' + ext;
            const fileData = await fetchFileData(job.file);
            await mainFFmpeg.writeFile(inputName, fileData);
  
            const duration = await getDuration(mainFFmpeg, inputName);
            const targetSegments = NUM_SEGMENTS;
            const segmentTime = duration > 0 ? Math.max(1, Math.ceil(duration / targetSegments)) : 0;
  
            if (duration === 0 || segmentTime <= 0) {
              job.phase = this.$t('lbl_EMPBE_VideoFormatter_Phase_ConvertingToMP4');
              job.segmentCount = 1;
  
              const preset = SPEED_PRESET;
              let peakProgress = 0;
              const progressHandler = (e) => {
                const p = e.progress != null ? e.progress : (e.time != null && duration > 0 ? e.time / duration : null);
                if (p != null) {
                  const val = Math.round(Math.max(0, Math.min(100, p * 100)));
                  if (val >= peakProgress) {
                    peakProgress = val;
                    job.progress = val;
                  }
                }
              };
              mainFFmpeg.on('progress', progressHandler);
  
              await mainFFmpeg.exec([
                '-i', inputName,
                '-c:v', 'libx264',
                '-vf', VIDEO_FILTER_SAME_RESOLUTION,
                '-preset', preset.preset,
                '-crf', preset.crf,
                '-c:a', 'aac',
                '-b:a', '128k',
                '-movflags', '+faststart',
                '-err_detect', 'ignore_err',
                '-fflags', '+genpts+discardcorrupt',
                '-r', '30',
                'output.mp4'
              ]);
  
              mainFFmpeg.off('progress', progressHandler);
  
              const output = await mainFFmpeg.readFile('output.mp4');
              const blob = new Blob([output.buffer], { type: 'video/mp4' });
  
              job.status = 'completed';
              job.progress = 100;
              job.outputBlob = blob;
              job.outputSize = blob.size;
              job.elapsed = this.formatDuration(performance.now() - startTime);
            } else {
              const numSegments = targetSegments;
              const numWorkers = Math.min(MAX_WORKERS, numSegments);
              job.segmentCount = numSegments;
  
              job.phase = this.$t('lbl_EMPBE_VideoFormatter_Phase_Splitting', { count: numSegments });
              job.progress = 5;
  
              await mainFFmpeg.exec([
                '-i', inputName,
                '-c', 'copy',
                '-f', 'segment',
                '-segment_time', String(segmentTime),
                '-reset_timestamps', '1',
                '-map', '0',
                'segment_%03d.' + ext
              ]);
  
              const segmentFiles = [];
              for (let i = 0; i < numSegments + 10; i++) {
                const segName = 'segment_' + String(i).padStart(3, '0') + '.' + ext;
                try {
                  const stat = await mainFFmpeg.readFile(segName);
                  if (stat && stat.length > 0) {
                    segmentFiles.push({ name: segName, data: stat });
                  } else break;
                } catch (_) {
                  break;
                }
              }
  
              const actualSegments = segmentFiles.length;
              job.segmentCount = actualSegments;
  
              if (actualSegments === 0) {
                throw new Error(this.$t('lbl_EMPBE_VideoFormatter_Phase_SplitFailed'));
              }

              if (actualSegments === 1) {
                job.phase = this.$t('lbl_EMPBE_VideoFormatter_Phase_ConvertingToMP4');
  
                const preset = SPEED_PRESET;
                let peakProgress2 = 10;
                const progressHandler = (e) => {
                  const p = e.progress != null ? e.progress : (e.time != null && duration > 0 ? e.time / duration : null);
                  if (p != null) {
                    const val = Math.round(10 + Math.max(0, Math.min(85, p * 85)));
                    if (val >= peakProgress2) {
                      peakProgress2 = val;
                      job.progress = val;
                    }
                  }
                };
                mainFFmpeg.on('progress', progressHandler);
  
                await mainFFmpeg.writeFile('seg_input.' + ext, segmentFiles[0].data);
                await mainFFmpeg.exec([
                  '-i', 'seg_input.' + ext,
                  '-c:v', 'libx264',
                  '-vf', VIDEO_FILTER_SAME_RESOLUTION,
                  '-preset', preset.preset,
                  '-crf', preset.crf,
                  '-c:a', 'aac',
                  '-b:a', '128k',
                  '-movflags', '+faststart',
                  '-err_detect', 'ignore_err',
                  '-fflags', '+genpts+discardcorrupt',
                  '-r', '30',
                  'output.mp4'
                ]);
  
                mainFFmpeg.off('progress', progressHandler);
  
                const output = await mainFFmpeg.readFile('output.mp4');
                const blob = new Blob([output.buffer], { type: 'video/mp4' });
  
                job.status = 'completed';
                job.progress = 100;
                job.outputBlob = blob;
                job.outputSize = blob.size;
                job.elapsed = this.formatDuration(performance.now() - startTime);
              } else {
                job.phase = this.$t('lbl_EMPBE_VideoFormatter_Phase_ConvertingParallel', { count: actualSegments });
                job.progress = 10;
  

                const instancePromises = [];
                for (let w = 0; w < numWorkers; w++) {
                  instancePromises.push(createFFmpegInstance());
                }
                const workerInstances = await Promise.all(instancePromises);
                workerInstances.forEach(inst => instancesToCleanup.push(inst));
  
                this.hideBanner();
  
                const segmentProgress = new Array(actualSegments).fill(0);
                const convertedSegments = new Array(actualSegments).fill(null);
                const preset = SPEED_PRESET;
                let peakOverall = 10;
  
                const updateOverallProgress = () => {
                  const avgProg = segmentProgress.reduce((a, b) => a + b, 0) / actualSegments;
                  const candidate = Math.min(90, Math.round(10 + (avgProg / 100) * 80));
                  if (candidate >= peakOverall) {
                    peakOverall = candidate;
                    job.progress = candidate;
                  }
                  const done = segmentProgress.filter(p => p >= 100).length;
                  job.phase = this.$t('lbl_EMPBE_VideoFormatter_Phase_ConvertingSegments', { done: done, total: actualSegments });
                };
  
                let nextSegIndex = 0;
                const activeSegIdx = new Map();
  
                const processSegment = async (workerIdx) => {
                  const worker = workerInstances[workerIdx];
                  const handler = (e) => {
                    const segIdx = activeSegIdx.get(workerIdx);
                    if (segIdx == null) return;
                    const p = e.progress != null ? e.progress : (e.time != null && segmentTime > 0 ? Math.min(1, e.time / segmentTime) : null);
                    if (p != null) {
                      const val = Math.max(0, Math.min(100, Math.round(p * 100)));
                      if (val >= segmentProgress[segIdx]) {
                        segmentProgress[segIdx] = val;
                        updateOverallProgress();
                      }
                    }
                  };
                  worker.on('progress', handler);
  
                  while (nextSegIndex < actualSegments) {
                    const segIdx = nextSegIndex++;
                    const seg = segmentFiles[segIdx];
                    const segInputName = 'seg_in_' + segIdx + '.' + ext;
                    const segOutputName = 'seg_out_' + segIdx + '.mp4';
  
                    activeSegIdx.set(workerIdx, segIdx);
  
                    await worker.writeFile(segInputName, seg.data);
  
                    await worker.exec([
                      '-i', segInputName,
                      '-c:v', 'libx264',
                      '-vf', VIDEO_FILTER_SAME_RESOLUTION,
                      '-preset', preset.preset,
                      '-crf', preset.crf,
                      '-c:a', 'aac',
                      '-b:a', '128k',
                      '-movflags', '+faststart',
                      '-err_detect', 'ignore_err',
                      '-fflags', '+genpts+discardcorrupt',
                      '-r', '30',
                      segOutputName
                    ]);
  
                    segmentProgress[segIdx] = 100;
                    activeSegIdx.delete(workerIdx);
                    updateOverallProgress();
  
                    const result = await worker.readFile(segOutputName);
                    convertedSegments[segIdx] = result;
  
                    try { await worker.deleteFile(segInputName); } catch (_) {}
                    try { await worker.deleteFile(segOutputName); } catch (_) {}
                  }
                };
  
                const workerPromises = [];
                for (let w = 0; w < numWorkers; w++) {
                  workerPromises.push(processSegment(w));
                }
                await Promise.all(workerPromises);
  
                job.phase = this.$t('lbl_EMPBE_VideoFormatter_Phase_Merging');
                job.progress = 92;
  
                const mergeFFmpeg = workerInstances[0];
                let concatList = '';
                for (let i = 0; i < actualSegments; i++) {
                  const partName = 'part_' + i + '.mp4';
                  await mergeFFmpeg.writeFile(partName, convertedSegments[i]);
                  concatList += "file '" + partName + "'\n";
                  convertedSegments[i] = null;
                }
  
                await mergeFFmpeg.writeFile('concat_list.txt', new TextEncoder().encode(concatList));
  
                await mergeFFmpeg.exec([
                  '-f', 'concat',
                  '-safe', '0',
                  '-i', 'concat_list.txt',
                  '-c', 'copy',
                  '-movflags', '+faststart',
                  'final_output.mp4'
                ]);
  
                const finalOutput = await mergeFFmpeg.readFile('final_output.mp4');
                const blob = new Blob([finalOutput.buffer], { type: 'video/mp4' });
  
                job.status = 'completed';
                job.progress = 100;
                job.outputBlob = blob;
                job.outputSize = blob.size;
                job.elapsed = this.formatDuration(performance.now() - startTime);
              }
            }
          } catch (err) {
            console.error('Conversion error:', err);
            job.status = 'error';
            job.error = classifyError(err, allLogs.join('\n'), this._errorMessages());
          } finally {
            for (let i = 0; i < instancesToCleanup.length; i++) {
              try { instancesToCleanup[i].terminate(); } catch (_) {}
            }
          }
  
          job.file = null;
          job.phase = '';
          this.hideBanner();
        }
  
        this.isConverting = false;
      },

      _progressMessages() {
        return {
          processingData: this.$t('lbl_EMPBE_VideoFormatter_Hint_ProcessingData'),
          optimizingQuality: this.$t('lbl_EMPBE_VideoFormatter_Hint_OptimizingQuality'),
          encodingSegments: this.$t('lbl_EMPBE_VideoFormatter_Hint_EncodingSegments'),
          compressingStream: this.$t('lbl_EMPBE_VideoFormatter_Hint_CompressingStream'),
          runningAlgorithms: this.$t('lbl_EMPBE_VideoFormatter_Hint_RunningAlgorithms'),
          adjustingFrameRates: this.$t('lbl_EMPBE_VideoFormatter_Hint_AdjustingFrameRates')
        };
      },

      _errorMessages() {
        return {
          corrupted: this.$t('lbl_EMPBE_VideoFormatter_Err_Corrupted'),
          unsupportedCodec: this.$t('lbl_EMPBE_VideoFormatter_Err_UnsupportedCodec'),
          invalidFile: this.$t('lbl_EMPBE_VideoFormatter_Err_InvalidFile'),
          engineFailed: this.$t('lbl_EMPBE_VideoFormatter_Err_EngineFailed'),
          defaultError: this.$t('lbl_EMPBE_VideoFormatter_Err_Default')
        };
      }
    }
  };
  </script>
  
  <style scoped lang="scss">
  .video-formatter-page {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    background: #f9fafb;
    color: #111827;
    font-family: 'Inter',  sans-serif;
  }
  
  .vf-header {
    border-bottom: 1px solid #e5e7eb;
    background: #fff;
    padding: 12px 1.5rem;
  }
  
  .vf-header-inner {
    max-width: 48rem;
    margin: 0 auto;
    display: flex;
    align-items: center;
    gap: 24px;
  }
  
  .vf-icon-wrap {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 0.5rem;
    background: #7c3aed;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .vf-icon {
    width: 1.25rem;
    height: 1.25rem;
    color: #fff;
  }
  
  .vf-title {
    font-size: 1.75rem;
    font-weight: 700;
    margin: 0;
    display: flex;
    justify-content: center;
  }
  
  .vf-subtitle {
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 130%; /* 18.2px */
    color: rgba(0, 0, 0, 0.40)
  }
  
  .vf-main {
    flex: 1;
    padding: 2rem 1.5rem;
  }
  
  .vf-container {
    max-width: 48rem;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
  
  .vf-loading-banner {
    border-radius: 0.5rem;
    border: 1px solid #c4b5fd;
    background: #f0f0ff;
    padding: 1rem;
  }
  
  .vf-loading-content {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  
  .vf-loading-title {
    font-size: 0.875rem;
    font-weight: 500;
    color: #5b21b6;
    margin: 0;
  }
  
  .vf-loading-subtitle {
    font-size: 0.75rem;
    color: #7c3aed;
    margin: 0;
  }
  
  .vf-spin {
    width: 1.25rem;
    height: 1.25rem;
    color: #7c3aed;
    animation: vf-spin 1s linear infinite;
  }
  
  .vf-opacity-25 { opacity: 0.25; }
  .vf-opacity-75 { opacity: 0.75; }
  
  @keyframes vf-spin {
    to { transform: rotate(360deg); }
  }
  
  .vf-card {
    border-radius: 0.75rem;
    border: 1px solid #e5e7eb;
    background: #fff;
    padding: 1.5rem;
    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  }
  
  .vf-dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    border: 2px dashed #d1d5db;
    border-radius: 0.5rem;
    padding: 2.5rem;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;
  }
  
  .vf-dropzone:hover {
    border-color: #9ca3af;
  }
  
  .vf-drop-active {
    border-color: #7c3aed !important;
    background: rgba(124, 58, 237, 0.05) !important;
  }
  
  .vf-drop-icon-wrap {
    width: 3.5rem;
    height: 3.5rem;
    border-radius: 50%;
    background: #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .vf-drop-icon {
    width: 1.5rem;
    height: 1.5rem;
    color: #9ca3af;
  }
  
  .vf-drop-text-wrap {
    text-align: center;
  }
  
  .vf-drop-text {
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin: 0;
  }
  
  .vf-drop-hint {
    font-size: 0.75rem;
    color: #6b7280;
    margin: 0.25rem 0 0;
  }
  
  .vf-browse-btn {
    padding: 0.375rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    background: #fff;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
  }
  
  .vf-browse-btn:hover {
    background: #f9fafb;
  }
  
  .vf-file-input {
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
    pointer-events: none;
  }
  
  .vf-jobs {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
  
  .vf-job-group {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .vf-group-title {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
    margin: 0;
    padding: 0 0.25rem;
  }
  
  .vf-job-card {
    border-radius: 0.75rem;
    border: 1px solid #e5e7eb;
    background: #fff;
    padding: 1rem;
    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;
  }
  
  .vf-job-icon {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 2px;
  }
  
  .vf-job-icon-loading {
    background: #e0e0ff;
    color: #7c3aed;
  }
  
  .vf-job-icon-done {
    background: #d1fae5;
    color: #059669;
    font-size: 0.875rem;
    font-weight: 600;
  }
  
  .vf-job-icon-error {
    background: #fee2e2;
    color: #dc2626;
    font-size: 0.875rem;
    font-weight: 600;
  }
  
  .vf-job-body {
    flex: 1;
    min-width: 0;
  }
  
  .vf-job-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }
  
  .vf-job-row-actions {
    flex-wrap: wrap;
  }
  
  .vf-job-filename {
    font-size: 0.875rem;
    font-weight: 500;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .vf-job-size {
    font-size: 0.75rem;
    color: #6b7280;
    flex-shrink: 0;
  }
  
  .vf-progress-bar {
    width: 100%;
    height: 0.5rem;
    background: #e5e7eb;
    border-radius: 9999px;
    overflow: hidden;
    margin-top: 0.5rem;
  }
  
  .vf-progress-fill {
    height: 100%;
    background: #7c3aed;
    border-radius: 9999px;
    transition: width 0.3s;
  }
  
  .vf-job-status {
    font-size: 0.75rem;
    color: #6b7280;
    margin: 0.25rem 0 0;
  }
  
  .vf-job-hint {
    font-size: 0.75rem;
    color: #9ca3af;
    font-style: italic;
    margin: 0.125rem 0 0;
  }
  
  .vf-job-meta {
    font-size: 0.75rem;
    color: #6b7280;
    margin: 0.25rem 0 0;
  }
  
  .vf-job-error {
    font-size: 0.75rem;
    color: #dc2626;
    margin: 0.25rem 0 0;
  }
  
  .vf-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
  }
  
  .vf-btn-dismiss {
    padding: 0.25rem 0.5rem;
    border: none;
    border-radius: 0.375rem;
    background: transparent;
    font-size: 0.75rem;
    color: #6b7280;
    cursor: pointer;
  }
  
  .vf-btn-dismiss:hover {
    background: #f3f4f6;
  }
  
  .vf-btn-download {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.75rem;
    border-radius: 0.375rem;
    background: #7c3aed;
    color: #fff;
    font-size: 0.75rem;
    font-weight: 500;
    border: none;
    cursor: pointer;
  }
  
  .vf-btn-download:hover {
    background: #6d28d9;
  }
  
  .vf-stay-notice {
    border-radius: 0.5rem;
    background: #fffbeb;
    border: 1px solid #fde68a;
    padding: 0.75rem 1rem;
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;
  }
  
  .vf-notice-icon {
    color: #d97706;
    flex-shrink: 0;
  }
  
  .vf-notice-title {
    font-size: 0.75rem;
    font-weight: 500;
    color: #92400e;
    margin: 0;
  }
  
  .vf-notice-text {
    font-size: 0.75rem;
    color: #b45309;
    margin: 0.25rem 0 0;
  }
  
  .vf-empty {
    text-align: center;
    padding: 2rem 0;
  }
  
  .vf-empty-icon {
    width: 3rem;
    height: 3rem;
    color: #d1d5db;
    margin: 0 auto;
    display: block;
  }
  
  .vf-empty-title {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0.25rem 0 0;
  }
  
  .vf-empty-text {
    font-size: 0.75rem;
    color: #9ca3af;
    margin: 0.25rem 0 0;
  }
  
  .vf-footer {
    border-top: 1px solid #e5e7eb;
    background: #fff;
    padding: 0.75rem 1.5rem;
  }
  
  .vf-footer-text {
    max-width: 48rem;
    margin: 0 auto;
    font-size: 0.75rem;
    color: #9ca3af;
    text-align: center;
  }
  
  .vf-modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0,0,0,0.4);
  }
  
  .vf-modal {
    background: #fff;
    border-radius: 0.75rem;
    box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);
    max-width: 24rem;
    width: 100%;
    margin: 0 1rem;
    overflow: hidden;
  }
  
  .vf-modal-body {
    padding: 1.25rem 1.25rem 1rem;
  }
  
  .vf-modal-icon-wrap {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    background: #fee2e2;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.75rem;
  }
  
  .vf-modal-icon {
    color: #dc2626;
    font-weight: 600;
  }
  
  .vf-modal-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.75rem;
  }
  
  .vf-modal-message {
    font-size: 0.875rem;
    color: #4b5563;
    margin: 0;

    .vf-modal-message-reject-reason {
      margin-top: 0.25rem;
    }
  }
  
  .vf-modal-footer {
    padding: 0 1.25rem 1rem;
  }
  
  .vf-btn-primary {
    width: 100%;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    background: #7c3aed;
    color: #fff;
    font-size: 0.875rem;
    font-weight: 500;
    border: none;
    cursor: pointer;
  }
  
  .vf-btn-primary:hover {
    background: #6d28d9;
  }
  </style>
  